import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, type AvatarShape } from "../../components/Avatar.js";
import { Icon } from "../../components/Icon.js";
import { GoalCard } from "../../components/GoalCard.js";
import { TxnRow } from "../../components/TxnRow.js";
import { fmt } from "../../lib/money.js";
import { listGoals, type Goal } from "../../api/goals.js";
import { sendKidStatement } from "../../api/statements.js";
import { useUser } from "../../auth/UserContext.js";
import ActionModal, { type ActionModalState } from "./ActionModal.js";
import BillsModal from "./BillsModal.js";
import type { ParentBundle } from "../../api/me.js";

export default function KidPreview() {
  const { kidId } = useParams<{ kidId: string }>();
  const navigate = useNavigate();
  const { bundle, refresh } = useUser();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendLabel, setSendLabel] = useState<"send" | "sent" | "failed">("send");
  const [modal, setModal] = useState<ActionModalState | null>(null);
  const [billsKid, setBillsKid] = useState<{ id: string; name: string } | null>(null);

  const handleSendStatement = async () => {
    if (!kidId || sending) return;
    setSending(true);
    try {
      await sendKidStatement(kidId);
      setSendLabel("sent");
      setTimeout(() => setSendLabel("send"), 3000);
    } catch {
      setSendLabel("failed");
      setTimeout(() => setSendLabel("send"), 3000);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!kidId) return;
    setGoalsLoading(true);
    listGoals(kidId)
      .then(setGoals)
      .catch(() => setGoals([]))
      .finally(() => setGoalsLoading(false));
  }, [kidId]);

  if (!bundle || bundle.user.role !== "parent") return null;
  const parent = bundle as ParentBundle;
  const kid = parent.family.find((k) => k.user.rowKey === kidId);

  if (!kid) {
    return (
      <div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate("/parent/dashboard")}
          style={{ marginBottom: 16 }}
        >
          <Icon name="chevronLeft" size={14} /> Back to dashboard
        </button>
        <p className="soft">Kid not found.</p>
      </div>
    );
  }

  const color = kid.user.color ?? "var(--brand)";
  const colorSoft = kid.user.colorSoft ?? "var(--brand-soft)";
  const avatarShape = (kid.user.avatarShape as AvatarShape) ?? "circle";

  const settings = parent.settings;
  const annualRate = settings.interestRateAnnualBasisPoints / 10000;
  const monthlyRate = annualRate / 12;
  const monthInterestCents = Math.round(kid.account.balanceCents * monthlyRate);

  const allocatedToGoals = goals.reduce((s, g) => s + g.savedCents, 0);
  const unallocated = kid.account.balanceCents - allocatedToGoals;

  const nextPaydayDays = (() => {
    const d = new Date();
    const next = new Date(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    return Math.ceil((next.getTime() - d.getTime()) / 86_400_000);
  })();
  const totalDaysInMonth = new Date(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth() + 1,
    0,
  ).getUTCDate();
  const elapsedDays = Math.max(0, totalDaysInMonth - nextPaydayDays);
  const paydayPct = totalDaysInMonth > 0 ? (elapsedDays / totalDaysInMonth) * 100 : 0;

  const recent = (kid.recentTxns ?? []).slice(0, 5);

  return (
    <div>
      {/* Header */}
      <div className="row-between" style={{ marginBottom: 24 }}>
        <div className="row">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate("/parent/dashboard")}>
            <Icon name="chevronLeft" size={14} /> Back
          </button>
          <Avatar kid={{ name: kid.user.name, color, avatarShape }} size={40} />
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>{kid.user.name}</h1>
            <div className="soft" style={{ fontSize: 12 }}>
              Age {kid.user.age ?? "—"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleSendStatement}
            disabled={sending}
            title="Send monthly statement to this kid (and yourself)"
          >
            <Icon name="mail" size={14} />
            {sendLabel === "sent" ? "Sent!" : sendLabel === "failed" ? "Failed" : "Send statement"}
          </button>
          <span className="chip brand">Parent preview</span>
        </div>
      </div>

      {/* Content mirroring kid Home */}
      <div className="hero-grid">
        {/* Balance hero */}
        <div
          className="card raised"
          style={{
            position: "relative",
            overflow: "hidden",
            padding: 36,
            background: `linear-gradient(135deg, ${colorSoft} 0%, white 60%)`,
          }}
        >
          <div
            className="decoration"
            style={{
              position: "absolute",
              top: -40,
              right: -40,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: color,
              opacity: 0.12,
            }}
          />
          <div
            className="decoration"
            style={{
              position: "absolute",
              top: 40,
              right: 40,
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: `3px solid ${color}`,
              opacity: 0.25,
            }}
          />
          <div style={{ position: "relative" }}>
            <div className="soft" style={{ fontSize: 14, marginBottom: 10 }}>
              Total balance
            </div>
            <div className="money money-xl" style={{ marginBottom: 16 }}>
              {fmt(kid.account.balanceCents)}
            </div>
            <div style={{ display: "flex", gap: 24, marginTop: 20 }}>
              <div>
                <div className="soft" style={{ fontSize: 12, marginBottom: 2 }}>
                  Free to spend
                </div>
                <div className="money money-md">{fmt(unallocated)}</div>
              </div>
              <div className="divider-v" />
              <div>
                <div className="soft" style={{ fontSize: 12, marginBottom: 2 }}>
                  Saved toward goals
                </div>
                <div className="money money-md">{fmt(allocatedToGoals)}</div>
              </div>
            </div>
            <div style={{ marginTop: 22, display: "flex", gap: 8 }}>
              <div className="chip brand">
                <Icon name="sparkle" size={12} />
                Earning {(annualRate * 100).toFixed(1)}% a year
              </div>
              <div className="chip">+{fmt(monthInterestCents)} this month</div>
            </div>
          </div>
        </div>

        {/* Right column: allowance + actions */}
        <div className="stack" style={{ gap: 16 }}>
          <div className="card">
            <div style={{ fontSize: 13, color: "var(--ink-mute)", marginBottom: 4 }}>
              Next allowance
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
              <div className="money money-lg">{fmt(kid.account.allowanceCents)}</div>
              <div className="soft" style={{ fontSize: 13 }}>
                in {nextPaydayDays} {nextPaydayDays === 1 ? "day" : "days"}
              </div>
            </div>
            <div className="progress">
              <div
                className="progress-fill"
                style={{ width: `${paydayPct}%`, background: color }}
              />
            </div>
            <div className="soft" style={{ fontSize: 12, marginTop: 8 }}>
              Paid on the 1st of every month
            </div>
          </div>

          <div className="card">
            <div style={{ fontSize: 13, color: "var(--ink-mute)", marginBottom: 10 }}>Actions</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <button
                className="btn btn-soft btn-sm"
                onClick={() => setModal({ type: "add", kidId: kidId! })}
              >
                <Icon name="plus" size={14} /> Add
              </button>
              <button
                className="btn btn-soft btn-sm"
                onClick={() => setModal({ type: "subtract", kidId: kidId! })}
              >
                <Icon name="minus" size={14} /> Subtract
              </button>
              <button
                className="btn btn-soft btn-sm"
                onClick={() => setModal({ type: "transfer", kidId: kidId! })}
              >
                <Icon name="transfer" size={14} /> Send
              </button>
              <button
                className="btn btn-soft btn-sm"
                onClick={() => setBillsKid({ id: kidId!, name: kid.user.name })}
              >
                <Icon name="minus" size={14} /> Bills
              </button>
            </div>
          </div>
        </div>

        {/* Goals with projections */}
        {!goalsLoading && goals.length > 0 && (
          <div className="card" style={{ gridColumn: "1 / -1" }}>
            <h3 style={{ marginBottom: 16 }}>Goals</h3>
            <div className={`grid grid-${Math.min(goals.length, 3) as 1 | 2 | 3}`}>
              {goals.map((g) => (
                <GoalCard key={g.rowKey} goal={g} kid={kid.user} account={kid.account} />
              ))}
            </div>
          </div>
        )}

        {/* Recent activity */}
        {recent.length > 0 && (
          <div className="card" style={{ gridColumn: "1 / -1" }}>
            <h3 style={{ marginBottom: 8 }}>Recent activity</h3>
            <div>
              {recent.map((t) => (
                <TxnRow key={t.rowKey} t={t} />
              ))}
            </div>
          </div>
        )}
      </div>

      {modal && (
        <ActionModal
          modal={modal}
          family={parent.family}
          onClose={() => setModal(null)}
          onCompleted={async () => {
            await refresh();
            setGoals((prev) => [...prev]);
          }}
        />
      )}
      {billsKid && (
        <BillsModal kidId={billsKid.id} kidName={billsKid.name} onClose={() => setBillsKid(null)} />
      )}
    </div>
  );
}
